import { useTranslation } from "_metronic/i18n/language";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ChangeEvent, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import SelectLanguage from "../../components/SelectLanguage";
import PagesStep, { PageInfo } from "../../components/Stage/PagesStage";
import { helperText, showMensage } from "app/services/FeaturesElement";
import {
  CampaignType,
  CognitiveCategoryType,
  QuestionType,
  QuizType,
  RelatedQuizType,
} from "app/types";
import SelectComponent from "./component/Select";
import { Stack } from "@mui/material";
import DialogSelectUsers from "./component/DialogSelectUsers";
import {
  AnanseApiService,
  CampaignApiServiceImpl,
  QuestionApiServiceImpl,
  QuizApiServiceImpl,
} from "app/services";
import QuestionsCloneList from "./component/Clone/QuestionsCloneList";
import Loading from "app/pages/home/components/Loading";
import CardInfoQuiz from "./component/Clone/CardInfoQuiz";
import CardListQuestions from "./component/Clone/CardListQuestions";
import { useTheme } from "styled-components";
import { QuestionApiService } from "app/services/Ananse/QuestionApiService";
import { Box, Button, Card, Form } from "../../components";
import { QuizApiService } from "app/services/Ananse/QuizApiService";
import QuestionForm from "../quiznew/update/QuestionForm";
import { LocaliseAPIService } from "app/services/Localise/localiseApi";
import useQuizFormModel from "../quiznew/hooks/useQuizFormNewModel";
import { getOtherQuizQuestionId } from "./utils";
import { getQuestionPackageSourceId } from "./utils/cloneQuestionPackages";
import { HideInputsKeysType } from "../quiznew/components/CardContentView";
import { hideInputs } from "../quiznew/quiznew-data";
import { schema } from "../quiznew/quiznew-schema";
import { useSelector } from "react-redux";
import { urlToFile } from "app/services/Features";
import { audiometryCategory } from "../quiznew/quiznew-view";

const api = new AnanseApiService();
const quizService = new QuizApiService();
const questionApi = new QuestionApiService();
const localiseApiService = LocaliseAPIService.getInstance();
const campaignService = new CampaignApiServiceImpl();
const quizServiceImpl = new QuizApiServiceImpl();
const questionServiceImpl = new QuestionApiServiceImpl();

const QuizClone = () => {
  const translate = useTranslation();
  const [questions, setQuestions] = useState<QuestionType[]>([]);
  const [campaign, setCampaign] = useState<CampaignType>({} as CampaignType);
  const [userIds, setUserIds] = useState<string>("");
  const [selectedQuestion, setSelectedQuestion] = useState<any | null>(null);
  const [cognitiveCategories, setCognitiveCategories] = useState<
    CognitiveCategoryType[]
  >([]);
  const [audiometryCategories, setAudiometryCategories] = useState<
    CognitiveCategoryType[]
  >([]);

  const locale = useSelector((state: any) => state.i18n.lang);
  const [openQuestionModal, setOpenQuestionModal] = useState(false);
  const [quizData, setQuizData] = useState<QuizType | null>(null);
  const [progressCloneQuestions, setProgressCloneQuestions] = useState<number>(
    0,
  );
  const [loading, setLoading] = useState<boolean>(false);
  const history = useHistory();
  const theme = useTheme();

  const model = useQuizFormModel({
    campaignService,
    quizService: quizServiceImpl,
    questionService: questionServiceImpl,
  });

  const { quizId, quizCloneId } = useParams<{
    quizId: string;
    quizCloneId: string;
  }>();
  const quizIdNumber = Number(quizId);
  const quizCloneIdNumber = Number(quizCloneId);

  const audioDevice = [
    {
      value: "speaker",
      name: translate("screens_table_audiometry_audio_device_type_speaker"),
    },
    {
      value: "headphones",
      name: translate("screens_table_audiometry_audio_device_type_headphones"),
    },
  ];

  const audioChannel = [
    {
      id: 1,
      value: "left",
      name: translate("screens_labels_left"),
    },
    {
      id: 2,
      value: "both",
      name: translate("screens_labels_both"),
    },
    {
      id: 3,
      value: "right",
      name: translate("screens_labels_right"),
    },
  ];

  const formInfo = useForm<QuizType>({
    resolver: yupResolver(schema),
    defaultValues: {
      quizId: 0,
      campaignId: 0,
      name: "",
      language: locale,
      publishedAt: new Date().toISOString(),
      type: "",
      maxShots: 0,
      userId: "",
      anamneseId: 0,
      campaign: {} as CampaignType,
      mandatoryAnamnese: false,
      relatedByQuiz: {} as RelatedQuizType,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    },
  });

  useEffect(() => {
    const startClone = async () => {
      const data = await localiseApiService.getCategories();
      if (data) {
        setCognitiveCategories(data);
      }

      setAudiometryCategories(audiometryCategory);

      if (quizIdNumber) {
        await getQuizById(quizIdNumber);
      }

      if (quizCloneIdNumber) {
        const quizClone = await quizService.getById(quizCloneIdNumber);
        setQuizData(quizClone);
        setCampaign(quizClone.campaign);
      }
    };

    startClone();
  }, [quizIdNumber, quizCloneIdNumber]);

  const onSubmit = async (data: any) => {
    const {
      name,
      campaignId,
      language,
      maxShots,
      type,
      anamneseId,
      mandatoryAnamnese,
      fragranceId,
      initialInformation,
      screeningId,
    } = data;

    const body = {
      name,
      campaignId,
      language,
      maxShots,
      type,
      anamneseId,
      mandatoryAnamnese,
      fragranceId,
      userId: userIds,
      initialInformation,
      screeningId,
    };

    try {
      setLoading(true);
      await cloneQuiz(body);
    } catch (e) {
      const apiMessage = (e as any)?.response?.data;
      await showMensage(
        translate,
        null,
        typeof apiMessage === "string"
          ? apiMessage
          : translate("screens.quiz.errors.create"),
      );
    } finally {
      setLoading(false);
    }
  };

  const cloneQuiz = async (body: any) => {
    if (quizIdNumber) {
      const reqBase = {
        method: "POST",
      };

      const response = await quizService.makeHttpRequest({
        ...reqBase,
        data: body,
      });

      if (response) {
        history.push(`/admin/quizclone/${quizIdNumber}/${response?.quizId}`);
      }
    }
  };

  const saveQuestions = async (questions: QuestionType[], quizId: number) => {
    if (!quizId) {
      throw new Error("O quiz clonado ainda não foi criado");
    }

    const totalQuestions = questions.length;
    const createdQuestions: Array<{
      source: QuestionType;
      created: QuestionType;
    }> = [];
    const quizAlreadyCreated = await quizService.getById(quizId);
    let existingQuestions: QuestionType[] = quizAlreadyCreated.questions || [];

    const findExistingQuestion = (source: QuestionType) =>
      existingQuestions.find(existing => {
        const hasSameQuestionId =
          !!source.questionId && existing.questionId === source.questionId;
        const hasSameSlotAndLabel =
          existing.arrangeQuestionSequence === source.arrangeQuestionSequence &&
          (existing.question?.quest || existing.quest) ===
            (source.question?.quest || source.quest);

        return hasSameQuestionId || hasSameSlotAndLabel;
      });

    const persistQuestion = async (
      source: QuestionType,
      existingQuestion?: QuestionType,
    ): Promise<QuestionType> => {
      const body = await formatQuestionInFormData(source, quizId, {
        createdQuestion: existingQuestion,
        includeFile: !existingQuestion,
      });

      return questionApi.makeHttpRequest({
        method: "POST",
        url: "/add-update",
        data: body,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    };

    for (let i = 0; i < questions.length; i++) {
      const question: QuestionType = questions[i];
      let existingQuestion = findExistingQuestion(question);
      let response: QuestionType;

      try {
        response = await persistQuestion(question, existingQuestion);
      } catch (error) {
        // Alguns erros da API podem acontecer depois da criação parcial do
        // vínculo. Recarrega o clone e conclui a pergunta como atualização.
        const refreshedQuiz = await quizService.getById(quizId);
        existingQuestions = refreshedQuiz.questions || [];
        existingQuestion = findExistingQuestion(question);

        if (!existingQuestion) {
          throw error;
        }

        response = await persistQuestion(question, existingQuestion);
      }

      if (!response?.quizQuestionId) {
        throw new Error("A API não retornou o ID da pergunta criada");
      }

      createdQuestions.push({ source: question, created: response });
      await onSaveImagesAnswers(question, response);
      setProgressCloneQuestions(
        totalQuestions ? ((i + 1) / totalQuestions) * 70 : 70,
      );
    }

    const newQuestionIdByPackageSource = new Map<number, number>();
    createdQuestions.forEach(({ source, created }) => {
      newQuestionIdByPackageSource.set(
        getQuestionPackageSourceId(source),
        created.quizQuestionId,
      );
    });

    for (let i = 0; i < createdQuestions.length; i++) {
      const { source, created } = createdQuestions[i];
      const hasQuestionNavigation = source.answers.some(
        answer => !!answer.nextQuizQuestionId,
      );

      if (hasQuestionNavigation) {
        const body = await formatQuestionInFormData(source, quizId, {
          createdQuestion: created,
          includeFile: false,
          newQuestionIdByPackageSource,
        });

        await questionApi.makeHttpRequest({
          method: "POST",
          url: "/add-update",
          data: body,
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      }

      setProgressCloneQuestions(70 + ((i + 1) / createdQuestions.length) * 30);
    }

    setProgressCloneQuestions(100);
    return true;
  };

  const onSaveImagesAnswers = async (
    question: QuestionType,
    response: QuestionType,
  ) => {
    if (!response?.answers) {
      throw new Error("A API não retornou as respostas da pergunta criada");
    }

    for (let index = 0; index < question.answers.length; index++) {
      const answer = question.answers[index];
      if (!answer.answer?.files?.length) {
        continue;
      }
      const file = answer.answer?.files?.[0];
      const tempUri = file?.tempUri;
      let fileToUpload = file?.file;

      if (tempUri) {
        try {
          const tempFile = await fetch(tempUri);
          const blob = await tempFile.blob();
          fileToUpload = new File([blob], file.name || "image.jpg", {
            type: blob.type,
          });
        } catch (error) {
          console.error("Erro ao baixar imagem de tempUri:", error);
          throw error;
        }
      }
      if (!fileToUpload) continue;

      const matchingAnswer =
        response.answers[index] ||
        response.answers.find(value => value.desc === answer.desc);
      const fileOwnerId = matchingAnswer?.quizQuestionAnswerId?.toString();
      if (!fileOwnerId) {
        throw new Error(
          `Resposta criada não encontrada para ${answer.answer?.desc ||
            answer.desc}`,
        );
      }

      const formData = new FormData();
      formData.append("file", fileToUpload);
      formData.append("fileOwnerId", fileOwnerId);

      try {
        await questionApi.makeHttpRequest({
          method: "POST",
          url: "/questionanswerfile",
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (error) {
        console.error("Erro ao enviar arquivo:", error);
        throw error;
      }
    }
  };

  const formatQuestionInFormData = async (
    data: QuestionType,
    quizId: number,
    options: {
      createdQuestion?: QuestionType;
      includeFile?: boolean;
      newQuestionIdByPackageSource?: Map<number, number>;
    } = {},
  ) => {
    const {
      createdQuestion,
      includeFile = false,
      newQuestionIdByPackageSource,
    } = options;
    const formData = new FormData();

    formData.append("quizId", quizId.toString());
    formData.append(
      "quizQuestionId",
      (createdQuestion?.quizQuestionId || 0).toString(),
    );
    formData.append(
      "questionId",
      (data.questionId || createdQuestion?.questionId || 0).toString(),
    );
    formData.append("quest", data.quest || data.question?.quest || "");
    formData.append("fragranceId", data.fragranceId || "");
    formData.append("rightAnswer", data.rightAnswer || "");
    formData.append("type", data.type || "");
    formData.append(
      "arrangeQuestionSequence",
      (data.arrangeQuestionSequence || 0).toString(),
    );
    formData.append(
      "categoryCognitiveQuestionId",
      (data?.categoryCognitiveQuestionId || "").toString(),
    );
    formData.append("timeInSeconds", (data?.timeInSeconds || "").toString());
    formData.append("instruction", data.instruction || "");

    // FILES
    if (includeFile && data.files && data.files.length) {
      const file = data.files[0];
      if (file) {
        if (file.file instanceof File) {
          formData.append("file.file", file.file);
          formData.append("file.name", file.name || file.file.name);
        } else if (file.tempUri) {
          const fileContent = await urlToFile(file.tempUri, file.name || "");
          formData.append("file.file", fileContent);
          formData.append("file.name", file.name || "");
        }
      }
    } else if (includeFile && data.file) {
      if (data.file.file instanceof File) {
        formData.append("file.file", data.file.file);
        formData.append("file.name", data.file.name || data.file.file.name);
      } else if (data.file.tempUri) {
        const fileContent = await urlToFile(
          data.file.tempUri,
          data.file.name || "",
        );
        formData.append("file.file", fileContent);
        formData.append("file.name", data.file.name || "");
      }
    }

    formData.append("description", data.description || "");

    // Mapeia as respostas
    data.answers.forEach((answer, index) => {
      const createdAnswer = createdQuestion?.answers?.[index];
      const remappedNextQuestionId = answer.nextQuizQuestionId
        ? newQuestionIdByPackageSource?.get(answer.nextQuizQuestionId) || 0
        : 0;

      formData.append(
        `answers[${index}][quizQuestionAnswerId]`,
        (createdAnswer?.quizQuestionAnswerId || 0).toString(),
      );
      formData.append(
        `answers[${index}][answerId]`,
        (createdAnswer?.answerId || answer.answerId || 0).toString(),
      );
      formData.append(`answers[${index}][desc]`, answer.desc || "");
      formData.append(
        `answers[${index}][description]`,
        answer.description || "",
      );
      formData.append(
        `answers[${index}][nextQuizQuestionId]`,
        remappedNextQuestionId.toString(),
      );
    });

    return formData;
  };

  const getQuizById = async (quizId: number) => {
    try {
      setLoading(true);
      const quiz = await quizService.getById(quizId);
      setCampaign(quiz.campaign);
      formInfo.setValue("name", quiz.name);
      formInfo.setValue("campaignId", quiz.campaignId);
      formInfo.setValue("language", quiz.language);
      // setLanguage(quiz.language);
      formInfo.setValue("publishedAt", quiz.publishedAt);
      formInfo.setValue("validatedAt", quiz.validatedAt);
      formInfo.setValue("type", quiz.type);
      formInfo.setValue("maxShots", quiz.maxShots);
      formInfo.setValue("screeningId", quiz.screeningId);
      setUserIds(quiz.userId);
      formInfo.setValue("anamneseId", quiz.anamneseId);
      formInfo.setValue("mandatoryAnamnese", quiz.mandatoryAnamnese);
      setQuestions(
        (quiz.questions || []).map((question: QuestionType) => ({
          ...question,
          cloneSourceQuizQuestionId: question.quizQuestionId,
        })),
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async (quizQuestionId: number) => {
    const result = await Swal.fire({
      title: translate("deleteRegister_title"),
      text: translate("deleteRegister_message"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: translate("buttons.confirm"),
      cancelButtonText: translate("buttons.cancel"),
    });

    if (result.isConfirmed) {
      try {
        setLoading(true);
        // await questionApi.delete(quizQuestionId);
        // await getQuizById(
        //   Number(quizIdNumber) || quizData?.quizId || formInfo.watch("quizId"),
        // );
        setQuestions(prev =>
          prev.filter(question => question.quizQuestionId !== quizQuestionId),
        );
        await showMensage(
          translate,
          translate("deleteRegister_deletedSuccess"),
          null,
        );
      } catch (error) {
        await showMensage(
          translate,
          null,
          translate("deleteRegister_deletedError"),
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const onEdit = (question: any) => {
    setSelectedQuestion(question);
    setOpenQuestionModal(true);
  };

  const onCleanQuestion = () => {
    setSelectedQuestion(null);
    setOpenQuestionModal(false);
  };

  const setNewQuestion = (question: QuestionType) => {
    setQuestions(prevQuestions => {
      const exists = prevQuestions.some(
        q => q.quizQuestionId === question.quizQuestionId,
      );

      if (exists) {
        return prevQuestions.map(quetionEdit =>
          quetionEdit.quizQuestionId === question.quizQuestionId
            ? {
                ...question,
                cloneSourceQuizQuestionId:
                  question.cloneSourceQuizQuestionId ??
                  getQuestionPackageSourceId(quetionEdit),
              }
            : quetionEdit,
        );
      } else {
        question.quizQuestionId = getOtherQuizQuestionId(prevQuestions);
        question.cloneSourceQuizQuestionId = question.quizQuestionId;
        return [...prevQuestions, question];
      }
    });
  };

  const handleFinishClone = async () => {
    if (loading || !quizCloneIdNumber) {
      return;
    }

    try {
      setLoading(true);
      const responseUpdateQuestions = await saveQuestions(
        questions,
        quizCloneIdNumber,
      );
      if (responseUpdateQuestions) {
        setLoading(false);
        await showMensage(
          translate,
          translate("defaultMessages.saveData"),
          null,
        );
        history.push(`/admin/quiz`);
      }
    } catch (e) {
      const apiMessage = (e as any)?.response?.data;
      await showMensage(
        translate,
        null,
        typeof apiMessage === "string"
          ? apiMessage
          : translate("screens.quiz.errors.create"),
      );
    } finally {
      setLoading(false);
    }
  };

  const showInput = (input: HideInputsKeysType): boolean => {
    const typeQuiz: number = Number(formInfo.watch("type")) || 0;
    return !hideInputs[input].includes(typeQuiz);
  };

  const quizCloneView = {
    ...(quizData || ({} as QuizType)),
    questions,
  } as QuizType;

  const pages: PageInfo[] = [
    {
      title: translate("screens_quiz_clone_label"),
      functionNextButton: async () => {
        const isValid = await formInfo.trigger();

        if (!userIds) {
          await showMensage(
            translate,
            null,
            "Selecione ao menos um usuário associado.",
          );
          return false;
        }

        const data = formInfo.getValues();

        isValid && (await onSubmit(data));

        return false;
      },
      chiildren: (
        <Card key={1} style={styles.cardStage}>
          <Card.Title>{translate("screens.quiz.labels.principal")}</Card.Title>

          <Card.Body>
            <Box
              style={{
                display: "grid",
                gridTemplateColumns: "50% 50%",
                gap: "20px",
                alignItems: "start",
              }}
            >
              <Form.Group>
                <Form.Label>
                  {translate("screens.quiz.labels.name")} *
                </Form.Label>
                <Form.Input
                  type="text"
                  autoComplete="off"
                  placeholder={translate("screens.quiz.placeholders.name")}
                  maxLength={100}
                  value={formInfo.watch("name")}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    formInfo.setValue("name", e.target.value);
                  }}
                  required
                />

                {formInfo.formState.errors.name &&
                  helperText(formInfo.formState.errors.name?.message || "")}
              </Form.Group>

              {/* Campanha */}
              <Form.Group>
                <Form.Label>
                  {translate("screens.quiz.labels.campaign")} *
                </Form.Label>
                {model.campaigns && (
                  <Form.Select
                    placeholder={translate("screens.quiz.labels.campaign")}
                    value={formInfo.watch("campaignId")}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                      const campaignId = Number(e.target.value);
                      formInfo.setValue("campaignId", campaignId);
                      const selectedCampaign = model.campaigns.find(
                        item => item.campaignId === campaignId,
                      );
                      if (selectedCampaign) {
                        setCampaign(selectedCampaign);
                      }
                    }}
                  >
                    <option value="">
                      {translate("screens.quiz.placeholders.campaign")}
                    </option>
                    {model.campaigns.map((campaign, index) => (
                      <option
                        key={`index${index}-${campaign.campaignId}`}
                        value={campaign.campaignId}
                      >
                        {campaign.name}
                      </option>
                    ))}
                  </Form.Select>
                )}

                {formInfo.formState.errors.campaignId &&
                  helperText(
                    formInfo.formState.errors.campaignId?.message || "",
                  )}
              </Form.Group>

              <Form.Group>
                <Form.Label>
                  {translate("screens.quiz.labels.language")}
                </Form.Label>
                <SelectLanguage
                  handleChangeLanguage={(
                    e: EventTarget & { value: string; name: string },
                  ) => formInfo.setValue("language", e.value)}
                  languageValue={formInfo.watch("language") || ""}
                />
                {formInfo.formState.errors.language &&
                  helperText(formInfo.formState.errors.language.message!)}
              </Form.Group>
              <Form.Group>
                <Form.Label>
                  {translate("screens.fragrance.labels.type")} *
                </Form.Label>
                <Form.Select
                  // selectData={selectTheTypeQuiz}
                  value={formInfo.watch("type") || ""}
                  onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                    formInfo.setValue(
                      "type",
                      e.target.value as
                        | ""
                        | "1"
                        | "2"
                        | "3"
                        | "4"
                        | "5"
                        | "6"
                        | "7"
                        | "8",
                    );
                  }}
                >
                  <option value={""}>
                    {translate("screens_quiz_selectTest_title")}
                  </option>
                  {model.selectTheTypeQuiz
                    .filter(value => value.key !== 2)
                    .map(value => (
                      <option key={value.key} value={value.key}>
                        {value.name}
                      </option>
                    ))}
                </Form.Select>
                {formInfo.formState.errors.type &&
                  helperText(formInfo.formState.errors.type.message!)}
              </Form.Group>
              {showInput("maxShots") && (
                <Form.Group>
                  <Form.Label>
                    {translate("screens.quiz.maxShotLabel")}
                  </Form.Label>
                  <Form.Input
                    type="number"
                    autoComplete="off"
                    name="maxShots"
                    placeholder={translate("screens.quiz.maxShotLabel")}
                    maxLength={100}
                    value={formInfo.watch("maxShots")}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => {
                      const value = Number(e.target.value);
                      formInfo.setValue("maxShots", value);
                    }}
                    required
                  />
                  {formInfo.formState.errors.maxShots &&
                    helperText(formInfo.formState.errors.maxShots.message!)}
                </Form.Group>
              )}

              {showInput("screening") && (
                <Form.Group>
                  <SelectComponent
                    url="quiz/anamnese"
                    placeholder={"Triagem"}
                    register={formInfo.register("screeningId")}
                    onChange={value => formInfo.setValue("screeningId", value)}
                    convertResultData={(data: any) => ({
                      value: data.quizId.toString(),
                      label: data.name,
                    })}
                    value={formInfo.watch("screeningId") || ""}
                  />
                </Form.Group>
              )}

              {showInput("anamnese") && (
                <Box
                  style={{
                    justifyContent: "start",
                    gap: "10px",
                    padding: "0",
                  }}
                >
                  <Form.Group style={{ width: "100%" }}>
                    <SelectComponent
                      url="quiz/anamnese"
                      placeholder={translate(
                        "screens.quiz.labels.selectAnamnesis",
                      )}
                      register={formInfo.register("anamneseId")}
                      onChange={value => formInfo.setValue("anamneseId", value)}
                      value={String(formInfo.watch("anamneseId"))}
                    />
                  </Form.Group>
                  {!!formInfo.watch("anamneseId") && (
                    <Form.Group style={{ width: "100%" }}>
                      <Form.Label>
                        {translate("screens_quiz_labels_anamnesisMandatory")}
                      </Form.Label>
                      <Form.Checkbox
                        checked={formInfo.watch("mandatoryAnamnese") || false}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                          formInfo.setValue(
                            "mandatoryAnamnese",
                            e.target.checked,
                          );
                        }}
                      />
                    </Form.Group>
                  )}
                </Box>
              )}
              <Form.Group>
                <Stack>
                  <Form.Label>
                    {translate("screens_quiz_users_associated")} *
                  </Form.Label>

                  <DialogSelectUsers
                    userIds={userIds}
                    setUserIds={setUserIds}
                  />
                  {formInfo.formState.errors.userId &&
                    helperText(formInfo.formState.errors.userId.message!)}
                </Stack>
              </Form.Group>
            </Box>
          </Card.Body>
        </Card>
      ),
    },
    {
      title: translate("screens_quiz_clone_resume_questions"),
      chiildren: (
        <Card key={2} style={styles.cardStage}>
          <Card.Title>
            {translate("screens.quiz.labels.questionarie")}
          </Card.Title>
          <Card.Body
            style={{
              padding: "0",
              maxHeight: "calc(100vh - 240px)",
              minHeight: "420px",
              overflowY: "auto",
              overscrollBehavior: "contain",
            }}
          >
            {quizCloneIdNumber || quizData?.quizId ? (
              <QuestionsCloneList
                questions={questions}
                setQuestions={setQuestions}
                collections={campaign.collection || []}
                quiz={quizCloneView}
                onEdit={onEdit}
                onDelete={onDelete}
                onAdd={() => setOpenQuestionModal(true)}
              />
            ) : null}

            <QuestionForm
              collections={campaign.collection || []}
              handleClose={() => setOpenQuestionModal(false)}
              cognitiveCategories={cognitiveCategories}
              audiometryCategories={audiometryCategories}
              audioDeviceType={audioDevice}
              audioChannel={audioChannel}
              open={openQuestionModal}
              question={selectedQuestion}
              onFinish={() => {
                // getQuizById(
                //   Number(quizIdNumber) ||
                //     quizData?.quizId ||
                //     formInfo.watch("quizId"),
                // );
                onCleanQuestion();
              }}
              quizId={quizCloneIdNumber || quizData?.quizId || 0}
              quiz={quizCloneView}
              returnInArray={true}
              setQuestion={setNewQuestion}
            />
          </Card.Body>
        </Card>
      ),
    },
    {
      title: translate("screens_quiz_clone_question"),
      chiildren: (
        <Card key={3} style={styles.cardStage}>
          <Card.Title>{translate("label_resume_change")}</Card.Title>
          <Card.Body style={{ maxHeight: "800px", overflowY: "auto" }}>
            <CardInfoQuiz quiz={quizCloneView} api={api} />
            <CardListQuestions
              questions={questions}
              translate={translate}
              collections={campaign.collection || []}
            />
          </Card.Body>
        </Card>
      ),
    },
  ];

  return (
    <>
      <Loading isLoading={loading} progress={progressCloneQuestions} />
      <PagesStep
        pagesInfo={pages}
        initialStage={quizCloneIdNumber ? 1 : 0}
        previousButtonShow={[2]}
        progressStageShow={true}
        finalButtonContent={
          <Button
            onClick={handleFinishClone}
            style={{ backgroundColor: theme.button, color: "#FFF" }}
            styleHover={{ backgroundColor: "transparent", color: theme.button }}
          >
            {translate("label_finish")}
          </Button>
        }
      />
    </>
  );
};

const styles: Record<string, React.CSSProperties> = {
  cardStage: {
    width: "100%",
  },
};

export default QuizClone;
